import { AxiosService } from 'src/axios/axios.service';
import { tAxiosCustomConfig } from 'src/axios/axios.types';
import { MediaType } from 'src/components/Chat/Chat.enum';
import { tAttachment } from 'src/components/Chat/Chat.types';
import { storageFileIdFromUrl } from 'src/functions/url.functions';

/**
 * BUG-1144: the browser names a saved file from `Content-Disposition` or, when
 * that header is absent, from the last segment of the URL. Our storage serves
 * media without the header under a ULID key, so every save landed as
 * `01M4REAEW5CZ02FB8MH8W3SB8E.pdf` instead of `ANEXO 3.pdf`.
 *
 * The `download` attribute cannot fix that on its own: the app runs on
 * `crm.leadsales.services` and the media on `leadsales.services`, and the
 * attribute is honored only same-origin (or for `blob:`/`data:`).
 *
 * So there are two rails, and `downloadAttachment` picks by scheme:
 *
 * - `https:` — our storage. The bytes are fetched and re-offered from a local
 *   blob URL, which is same-origin and therefore carries the name we ask for.
 * - `blob:` — an attachment a lead sent over WhatsApp Web, which reaches us
 *   already decrypted into an object URL. It is same-origin as it stands, so
 *   it goes straight to the anchor with no fetch at all.
 */

// Crockford base32, the ULID alphabet. The first 10 characters of a ULID encode
// its creation time in milliseconds, which is when we stored the file.
const ULID_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const ULID_TIMESTAMP_LENGTH = 10;

const MEDIA_KIND_NAMES: Partial<Record<MediaType, string>> = {
  [MediaType.IMAGE]: 'imagen',
  [MediaType.VIDEO]: 'video',
  [MediaType.AUDIO]: 'audio',
  [MediaType.STICKER]: 'sticker',
  [MediaType.DOCUMENT]: 'documento',
};
const DEFAULT_MEDIA_KIND_NAME = 'archivo';

function ulidTimestamp(fileId: string): number | null {
  const head = fileId.slice(0, ULID_TIMESTAMP_LENGTH).toUpperCase();
  if (head.length < ULID_TIMESTAMP_LENGTH) return null;

  let milliseconds = 0;
  for (const character of head) {
    const value = ULID_ALPHABET.indexOf(character);
    if (value < 0) return null;
    milliseconds = milliseconds * 32 + value;
  }
  return milliseconds;
}

/**
 * `YYYY-MM-DD-HHmm` in UTC. Deliberately not the viewer's timezone: the name is
 * only there to be legible and sortable, and a local-time name would differ
 * between two people looking at the same file.
 */
function stamp(milliseconds: number): string {
  const date = new Date(milliseconds);
  const pad = (value: number) => String(value).padStart(2, '0');
  return [
    date.getUTCFullYear(),
    pad(date.getUTCMonth() + 1),
    pad(date.getUTCDate()),
  ].join('-')
    .concat('-', pad(date.getUTCHours()), pad(date.getUTCMinutes()));
}

function extensionFromUrl(url: string | null): string {
  if (!url) return '';
  let pathname: string;
  try {
    pathname = new URL(url).pathname;
  } catch {
    pathname = url;
  }
  const lastSegment = pathname.slice(pathname.lastIndexOf('/') + 1);
  const dot = lastSegment.lastIndexOf('.');
  if (dot <= 0) return '';
  return lastSegment.slice(dot + 1).toLowerCase();
}

function extensionFromMimeType(mimeType: string | null): string {
  if (!mimeType) return '';
  // A media type is case-insensitive (RFC 2045), so `image/PNG` is as legal as
  // `image/png` and must yield the same extension.
  const subtype = mimeType.toLowerCase().split(';')[0].split('/')[1] ?? '';
  // Only the short, already-extension-shaped subtypes (png, mp4, pdf). The long
  // vendor ones (`vnd.openxmlformats-…`) are not extensions and are skipped.
  return /^[a-z0-9]{1,5}$/.test(subtype) ? subtype : '';
}

/** The basename, so a crafted name cannot walk out of the download folder. */
function basename(filename: string): string {
  return filename.split(/[\\/]/).pop() ?? '';
}

/**
 * The name to save an attachment under: the one the sender gave it when there
 * is one, otherwise a legible name built from the media kind and the time we
 * stored the file. Images arrive with an empty `filename` — WhatsApp does not
 * carry one for them — so for those the fallback is the only name there is.
 *
 * Known limitation: the date needs the ULID, which only an https storage URL
 * carries. A WhatsApp Web attachment reaches us as a `blob:` URL whose path is
 * an opaque uuid, so an unnamed one falls back to the bare kind (`imagen.jpeg`)
 * and several in a row collide into `imagen (1).jpeg`. Documents are unaffected
 * — they carry the sender's name — and a bare kind still beats the uuid the
 * browser would otherwise use. Dating those would mean threading the message
 * through `parseSlides`' nine call sites; the download timestamp is not a
 * substitute, since a batch of photos saved together shares one minute anyway.
 */
export function resolveAttachmentFilename(
  attachment: Pick<tAttachment, 'filename' | 'mimeType' | 'url' | 'mediaType'>,
): string {
  const kind = MEDIA_KIND_NAMES[attachment.mediaType] ?? DEFAULT_MEDIA_KIND_NAME;
  const extension =
    extensionFromUrl(attachment.url) || extensionFromMimeType(attachment.mimeType);
  const suffix = extension ? `.${extension}` : '';

  const given = basename((attachment.filename ?? '').trim());
  if (given) {
    // WhatsApp lets a document through with a bare name. Saved verbatim it has
    // no extension and the OS cannot associate it with an app, so give it the
    // one we know — the browser used to supply it through the ULID key.
    return given.includes('.') ? given : `${given}${suffix}`;
  }

  const fileId = storageFileIdFromUrl(attachment.url);
  const milliseconds = fileId ? ulidTimestamp(fileId) : null;
  if (milliseconds === null) return `${kind}${suffix}`;

  return `${kind}-${stamp(milliseconds)}${suffix}`;
}

/**
 * Saves `url` to the viewer's device under `filename`.
 *
 * Rejects instead of throwing synchronously so a caller can render its own
 * error state. The scheme guard keeps a chat-sourced URL — attacker-influenced
 * data — from reaching an `<a href>` sink under a `javascript:` scheme
 * (BUG-712). `blob:` is allowed beside `https:` because an encrypted WhatsApp
 * Web attachment reaches us already decrypted into an object URL
 * (`Chat.actions.decryptAttachment`), which is same-origin by construction.
 */
export async function downloadAttachment(
  url: string,
  filename: string,
): Promise<void> {
  let scheme = '';
  try {
    scheme = new URL(url).protocol;
  } catch {
    scheme = '';
  }
  if (scheme !== 'https:' && scheme !== 'blob:') {
    throw new Error(
      'downloadAttachment: only https and blob URLs can be downloaded',
    );
  }

  // A blob: URL already holds the bytes in this page and is same-origin, so
  // there is nothing to fetch — and nothing we may revoke: `blobUrlRegistry`
  // handed the same URL to the bubble that renders the attachment.
  //
  // Sending it through AxiosService instead would 404: the config below sets
  // `baseURL`, and axios reads `blob:https://…` as a relative path because its
  // absolute-URL test wants `scheme://`, which `blob:` is not followed by.
  //
  // This branch cannot reject, so a caller's error state is unreachable on it.
  // That is the honest shape: a programmatic `<a download>` click reports
  // nothing back, so an already-revoked object URL is a silent no-op either
  // way, and the open-in-a-tab fallback would point at the same dead URL.
  if (scheme === 'blob:') {
    saveFromHref(url, filename);
    return;
  }

  const config: tAxiosCustomConfig = {
    responseType: 'blob',
    headers: {},
    isLegacy: true,
    isPublicAsset: true,
    // The caller renders its own error state, and a failure here is routine
    // (an expired signed URL, a content blocker, no connection) rather than a
    // defect — capturing every one would drown the project's Sentry.
    skipSentryCapture: true,
  };
  const blob = await AxiosService.get<Blob>(url, config);

  const objectUrl = URL.createObjectURL(blob);
  saveFromHref(objectUrl, filename);
  // Ours to release: nothing else holds this one.
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

/** Hands `href` to the browser as a save named `filename`. */
function saveFromHref(href: string, filename: string): void {
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
}
